(() => {
  'use strict';

  const SELECTOR = '[data-cut-builder-v2]';

  class CustomCutCalculatorV2 {
    constructor(builder) {
      this.builder = builder;
      this.sectionId = builder.dataset.sectionId;
      this.productInfo = builder.closest('product-info');
      this.form = document.getElementById(builder.dataset.productFormId);
      this.configElement = document.getElementById(`cut-pricing-config-v2-${this.sectionId}`);
      this.rowsElement = builder.querySelector('[data-cut-rows-v2]');
      this.template = document.getElementById(`CustomCutRowV2-${this.sectionId}`);
      this.messageElement = builder.querySelector('[data-cut-message-v2]');
      this.propertiesContainer = this.form?.querySelector('[data-v2-dynamic-properties]');
      this.materialElement = builder.querySelector('[data-material-total-v2]');
      this.cutChargeElement = builder.querySelector('[data-cut-charge-v2]');
      this.totalElement = builder.querySelector('[data-config-total-v2]');
      this.sheetCountElement = builder.querySelector('[data-sheet-count-v2]');
      this.layoutContainer = builder.querySelector('[data-sheet-layouts-v2]');
      this.mainTotalElement = this.productInfo?.querySelector('#totalPriceV2');
      this.currentLayoutPdfBlob = null;
      this.layoutPdfUrl = null;
      this.config = this.readConfig();
      this.lastResult = null;
      this.unsubscribeVariant = null;
      this.currentVariantAvailable = this.getSelectedVariantData()?.available !== false;

      if (!this.rowsElement || !this.template || !this.form || !this.configElement) {
        console.error('V2 custom-cut calculator could not initialize because required markup is missing.');
        return;
      }

      this.bindEvents();
      this.addRow();
      this.observeVariantPrice();
      this.subscribeToVariantChanges();
      this.calculate({ validate: false });
      builder.dataset.initialized = 'true';
    }

    readConfig() {
      const el = this.configElement;
      const number = (value, fallback = 0) => {
        const parsed = Number.parseFloat(value);
        return Number.isFinite(parsed) ? parsed : fallback;
      };

      const roundSet = String(el?.dataset.roundSet || '')
        .split(',')
        .map((value) => number(value, NaN))
        .filter(Number.isFinite)
        .sort((a, b) => a - b);

      const roundingOffset = number(el?.dataset.roundingOffset, 0.124);

      return {
        roundSet,
        maxLength: number(el?.dataset.maxL, 96),
        maxWidth: number(el?.dataset.maxW, 48),
        roundingOffset,
        // Saw-blade kerf is a physical spacing rule and is intentionally independent
        // of the billable-size rounding setting.
        kerf: Math.max(0, number(el?.dataset.kerf, 0.124)),
        allowRotation: el?.dataset.allowRotation !== 'false',
        maxPackingPieces: Math.max(1, Math.floor(number(el?.dataset.maxPackingPieces, 1500))),
        cutRates: {
          1: number(el?.dataset.cutPrice1, 10),
          2: number(el?.dataset.cutPrice2, 5),
          3: number(el?.dataset.cutPrice3, 4),
          4: number(el?.dataset.cutPrice4, 3),
          5: number(el?.dataset.cutPrice5, 2.5),
          sixPlus: number(el?.dataset.cutPrice6Plus, 2),
        },
      };
    }

    bindEvents() {
      this.builder.querySelector('[data-add-cut-v2]')?.addEventListener('click', () => this.addRow());
      this.builder.querySelector('[data-calculate-cuts-v2]')?.addEventListener('click', () => {
        this.calculate({ validate: true, focusError: true });
      });

      this.rowsElement.addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove-cut-v2]');
        if (!button) return;
        const row = button.closest('[data-cut-row-v2]');
        if (!row) return;

        if (this.rowsElement.querySelectorAll('[data-cut-row-v2]').length === 1) {
          this.resetRow(row);
        } else {
          row.remove();
        }
        this.renumberRows();
        this.calculate({ validate: false });
      });

      const recalculate = () => this.calculate({ validate: false });
      this.rowsElement.addEventListener('input', recalculate);
      this.rowsElement.addEventListener('change', recalculate);

      this.form.addEventListener('submit', (event) => {
        const result = this.calculate({ validate: true, focusError: true });
        if (!result) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
      }, true);

      // Dawn's product form builds a FormData object for AJAX add-to-cart.
      // Append the generated PDF directly at that moment so it is stored as a
      // standard Shopify file line-item property without requiring a visible file input.
      this.form.addEventListener('formdata', (event) => {
        if (!this.currentLayoutPdfBlob || !this.lastResult?.cuts?.length) return;
        const sheetCount = this.lastResult.packing?.sheetCount || 1;
        const filename = `cut-layout-${sheetCount}-sheet${sheetCount === 1 ? '' : 's'}.pdf`;
        event.formData.set('properties[Cut Layout PDF]', this.currentLayoutPdfBlob, filename);
      });

      this.productInfo?.querySelector('.qty__input')?.addEventListener('input', recalculate);
      this.productInfo?.querySelector('.qty__input')?.addEventListener('change', recalculate);

      const clearButton = this.productInfo?.querySelector('[data-cut-clear-v2]');
      clearButton?.addEventListener('click', () => this.clearAll());

      this.productInfo?.addEventListener('change', (event) => {
        if (event.target.closest('variant-selects') && !event.target.closest('[data-cut-builder-v2]')) {
          window.setTimeout(() => this.calculate({ validate: false }), 80);
        }
      });
    }

    subscribeToVariantChanges() {
      try {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data || data.sectionId === this.sectionId) {
              if (data?.variant) this.currentVariantAvailable = data.variant.available !== false;
              window.setTimeout(() => this.calculate({ validate: false }), 0);
            }
          });
        }
      } catch (error) {
        console.warn('V2 calculator could not subscribe to variant changes.', error);
      }
    }

    observeVariantPrice() {
      const priceContainer = this.productInfo?.querySelector(`#price-${CSS.escape(this.sectionId)}`);
      if (!priceContainer || !('MutationObserver' in window)) return;

      this.priceObserver = new MutationObserver(() => this.calculate({ validate: false }));
      this.priceObserver.observe(priceContainer, { childList: true, subtree: true, attributes: true });
    }

    addRow(values = {}) {
      const fragment = this.template.content.cloneNode(true);
      const row = fragment.querySelector('[data-cut-row-v2]');
      if (!row) return;

      row.querySelector('[data-cut-width-v2]').value = values.width ?? '';
      row.querySelector('[data-cut-length-v2]').value = values.length ?? '';
      row.querySelector('[data-cut-pieces-v2]').value = values.pieces ?? 1;
      row.querySelector('[data-cut-tolerance-v2]').value = values.tolerance ?? '';
      this.rowsElement.appendChild(fragment);
      this.renumberRows();
    }

    resetRow(row) {
      row.querySelector('[data-cut-width-v2]').value = '';
      row.querySelector('[data-cut-length-v2]').value = '';
      row.querySelector('[data-cut-pieces-v2]').value = '1';
      row.querySelector('[data-cut-tolerance-v2]').value = '';
      row.querySelectorAll('.input-error').forEach((input) => input.classList.remove('input-error'));
    }

    clearAll() {
      this.rowsElement.innerHTML = '';
      this.addRow();
      this.hideMessage();
      this.calculate({ validate: false });
    }

    renumberRows() {
      this.rowsElement.querySelectorAll('[data-cut-row-v2]').forEach((row, index) => {
        row.dataset.cutIndex = String(index + 1);
        const removeButton = row.querySelector('[data-remove-cut-v2]');
        if (removeButton) removeButton.setAttribute('aria-label', `Remove cut ${index + 1}`);
      });
    }

    hasCutIntent() {
      const rows = [...this.rowsElement.querySelectorAll('[data-cut-row-v2]')];

      return rows.some((row) => {
        const width = String(row.querySelector('[data-cut-width-v2]')?.value || '').trim();
        const length = String(row.querySelector('[data-cut-length-v2]')?.value || '').trim();
        const tolerance = String(row.querySelector('[data-cut-tolerance-v2]')?.value || '').trim();
        const pieces = String(row.querySelector('[data-cut-pieces-v2]')?.value || '').trim();

        // A new row starts with Pieces = 1. That default alone does not mean
        // the customer wants cuts; any other entered cut value does.
        return width !== '' || length !== '' || tolerance !== '' || (pieces !== '' && pieces !== '1');
      });
    }

    clearCutInputErrors() {
      this.rowsElement.querySelectorAll('.input-error').forEach((input) => input.classList.remove('input-error'));
    }

    getProductVariants() {
      const data = this.productInfo?.querySelector('variant-selects [data-product-variants-v2]');
      if (!data?.textContent.trim()) return [];

      try {
        const variants = JSON.parse(data.textContent);
        return Array.isArray(variants) ? variants : [];
      } catch (_) {
        return [];
      }
    }

    getSelectedOptionValues() {
      const variantSelects = this.productInfo?.querySelector('variant-selects');
      if (!variantSelects) return [];

      const controls = [...variantSelects.querySelectorAll('[name^="options["]')];
      const optionOrder = [];
      const selectedByName = new Map();

      controls.forEach((control) => {
        const name = this.getOptionName(control);
        if (!name) return;
        if (!optionOrder.includes(name)) optionOrder.push(name);

        if (control.matches('select')) {
          selectedByName.set(name, control.value);
        } else if (control.checked) {
          selectedByName.set(name, control.value);
        }
      });

      return optionOrder.map((name) => String(selectedByName.get(name) ?? ''));
    }

    getVariantFromSelectedOptions() {
      const selectedOptions = this.getSelectedOptionValues();
      if (!selectedOptions.length) return null;

      return this.getProductVariants().find((variant) => {
        if (!Array.isArray(variant?.options) || variant.options.length !== selectedOptions.length) return false;
        return variant.options.every((value, index) => String(value) === selectedOptions[index]);
      }) || null;
    }

    getUnitPrice() {
      // V2 full-sheet products always use the selected Shopify variant price
      // as the material price for one complete sheet.
      const matchedVariant = this.getVariantFromSelectedOptions();
      if (Number.isFinite(matchedVariant?.price)) return matchedVariant.price / 100;

      const visiblePrice = this.productInfo?.querySelector('.main_var_price[data-price]');
      const cents = Number(visiblePrice?.dataset.price);
      if (Number.isFinite(cents) && cents >= 0) return cents / 100;

      const selectedVariant = this.productInfo?.querySelector('variant-selects [data-selected-variant]');
      if (selectedVariant?.textContent.trim()) {
        try {
          const variant = JSON.parse(selectedVariant.textContent);
          if (Number.isFinite(variant?.price)) return variant.price / 100;
        } catch (_) {}
      }
      return 0;
    }

    getFullSheetMaterialPrice(_sheet) {
      // In the current V3 full-sheet flow, the selected Shopify variant price
      // is the price of one complete sheet. Multi-sheet jobs multiply this value
      // by the number of sheets required by the nesting plan.
      const variantPrice = this.getUnitPrice();
      return Number.isFinite(variantPrice) && variantPrice >= 0 ? variantPrice : 0;
    }

    roundUpToSet(value, cap) {
      let number = Number.parseFloat(value);
      if (!Number.isFinite(number) || number <= 0) return 0;
      number = Math.min(number, cap);

      if (!this.config.roundSet.length) return number;
      for (const size of this.config.roundSet) {
        if (size > cap) break;
        if (number <= size - this.config.roundingOffset - 1e-9) return size;
      }

      // A future sheet may be longer/wider than the current breakpoint list.
      // Falling back to the requested dimension avoids using a smaller price size.
      return number;
    }

    getCutRate(pieces) {
      if (pieces >= 6) return this.config.cutRates.sixPlus;
      return this.config.cutRates[pieces] ?? this.config.cutRates.sixPlus;
    }

    getSelectedVariantData() {
      const selectedVariant = this.productInfo?.querySelector('variant-selects [data-selected-variant]');
      if (!selectedVariant?.textContent.trim()) return null;

      try {
        return JSON.parse(selectedVariant.textContent);
      } catch (_) {
        return null;
      }
    }

    getOptionName(input) {
      const name = String(input?.getAttribute('name') || '');
      const match = name.match(/^options\[(.*)]$/i);
      return match ? match[1].trim() : '';
    }

    isSheetSizeOptionName(name) {
      const normalized = String(name || '')
        .toLowerCase()
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

      return normalized === 'size' || normalized === 'sheet size' || normalized.includes('sheet size');
    }

    parseImperialDimension(value) {
      const text = String(value || '')
        .replace(/[″”]/g, '"')
        .replace(/\b(?:inches|inch|in)\b/gi, '')
        .replace(/"/g, '')
        .trim();

      if (!text) return NaN;

      const mixed = text.match(/(\d+(?:\.\d+)?)\s*[- ]\s*(\d+)\s*\/\s*(\d+)/);
      if (mixed) {
        const whole = Number.parseFloat(mixed[1]);
        const numerator = Number.parseFloat(mixed[2]);
        const denominator = Number.parseFloat(mixed[3]);
        return denominator > 0 ? whole + (numerator / denominator) : NaN;
      }

      const fraction = text.match(/(\d+)\s*\/\s*(\d+)/);
      if (fraction) {
        const numerator = Number.parseFloat(fraction[1]);
        const denominator = Number.parseFloat(fraction[2]);
        return denominator > 0 ? numerator / denominator : NaN;
      }

      const decimal = text.match(/\d+(?:\.\d+)?/);
      return decimal ? Number.parseFloat(decimal[0]) : NaN;
    }

    parseSheetSize(value) {
      const raw = String(value || '').trim();
      if (!raw) return null;

      const normalized = raw.replace(/&quot;/gi, '"').replace(/[×]/g, 'x');
      const parts = normalized.split(/\s*(?:x|\bby\b)\s*/i);
      if (parts.length < 2) return null;

      const width = this.parseImperialDimension(parts[0]);
      const length = this.parseImperialDimension(parts[1]);
      if (!(width > 0 && length > 0)) return null;

      return { width, length, label: raw };
    }

    getSheetSize() {
      const fallback = {
        width: this.config.maxWidth,
        length: this.config.maxLength,
        label: `${this.cleanNumber(this.config.maxWidth)}\" x ${this.cleanNumber(this.config.maxLength)}\"`,
      };

      const inputs = [...(this.productInfo?.querySelectorAll('variant-selects [name^="options["]') || [])];
      const sizeInputs = inputs.filter((input) => this.isSheetSizeOptionName(this.getOptionName(input)));
      if (!sizeInputs.length) return fallback;

      let selectedInput = sizeInputs.find((input) => input.matches('select'));
      if (!selectedInput) selectedInput = sizeInputs.find((input) => input.checked) || sizeInputs[0];

      let value = '';
      if (selectedInput?.matches('select')) {
        value = selectedInput.selectedOptions?.[0]?.textContent || selectedInput.value;
      } else {
        value = selectedInput?.value || '';
      }

      return this.parseSheetSize(value) || fallback;
    }

    getToleranceDimensionAdjustment(tolerance) {
      const normalized = String(tolerance || '')
        .toLowerCase()
        .replace(/±/g, '+/-')
        .replace(/[″”]/g, '"')
        .replace(/\\/g, '')
        .replace(/\s+/g, ' ')
        .trim();

      // Labels can include extra text such as "- Nominal". The 1/8 fraction
      // alone triggers the 0.125" reduction used only for sheet-fit logic.
      return /1\s*\/\s*8/.test(normalized) ? 0.125 : 0;
    }

    getPackingDimensions(width, length, tolerance) {
      const adjustment = this.getToleranceDimensionAdjustment(tolerance);
      return {
        width: width - adjustment,
        length: length - adjustment,
        adjustment,
      };
    }

    getOversizeSupportMessage(sheet) {
      const selectedSheet = sheet?.label || '48" x 96"';
      return `These dimensions do not fit within the selected ${selectedSheet} sheet. For sizes larger than 48" x 96", please call 303-781-1171.`;
    }

    pieceFitsSheet(width, length, sheet) {
      const epsilon = 1e-7;
      const pieceLongSide = Math.max(width, length);
      const pieceShortSide = Math.min(width, length);
      const sheetLongSide = Math.max(sheet.width, sheet.length);
      const sheetShortSide = Math.min(sheet.width, sheet.length);

      return pieceLongSide <= sheetLongSide + epsilon
        && pieceShortSide <= sheetShortSide + epsilon;
    }

    normalizePackingToSheet(width, length, sheet) {
      return {
        short: Math.min(width, length),
        long: Math.max(width, length),
        sheetShort: Math.min(sheet.width, sheet.length),
        sheetLong: Math.max(sheet.width, sheet.length),
      };
    }

    buildPackingPieces(cuts, sheet) {
      const kerf = this.config.kerf;
      const pieces = [];
      let totalPieces = 0;

      for (const cut of cuts) {
        totalPieces += cut.pieces;
        if (totalPieces > this.config.maxPackingPieces) {
          return {
            error: `The calculator can nest up to ${this.config.maxPackingPieces} pieces at one time. Please contact us for larger production runs.`,
          };
        }

        const normalized = this.normalizePackingToSheet(cut.packingWidth, cut.packingLength, sheet);
        for (let pieceIndex = 0; pieceIndex < cut.pieces; pieceIndex += 1) {
          pieces.push({
            id: `${cut.number}-${pieceIndex + 1}`,
            cutNumber: cut.number,
            pieceNumber: pieceIndex + 1,
            nominalWidth: cut.width,
            nominalHeight: cut.length,
            packingShort: normalized.short,
            packingLong: normalized.long,
            width: normalized.short + kerf,
            height: normalized.long + kerf,
          });
        }
      }

      return {
        pieces,
        totalPieces,
        kerf,
        sheetShort: Math.min(sheet.width, sheet.length),
        sheetLong: Math.max(sheet.width, sheet.length),
      };
    }

    tryUniformGridMultiSheet(pieces, binWidth, binHeight) {
      if (!pieces.length) return [];

      const epsilon = 1e-7;
      const first = pieces[0];
      const sameSize = pieces.every((piece) =>
        Math.abs(piece.width - first.width) <= epsilon
        && Math.abs(piece.height - first.height) <= epsilon
      );
      if (!sameSize) return null;

      const columns = Math.floor((binWidth + epsilon) / first.width);
      const rows = Math.floor((binHeight + epsilon) / first.height);
      const capacity = columns * rows;
      if (capacity < 1) return null;

      const sheets = [];
      pieces.forEach((piece, index) => {
        const sheetIndex = Math.floor(index / capacity);
        const position = index % capacity;
        if (!sheets[sheetIndex]) sheets[sheetIndex] = { placements: [] };

        sheets[sheetIndex].placements.push({
          ...piece,
          x: (position % columns) * piece.width,
          y: Math.floor(position / columns) * piece.height,
          rotated: false,
        });
      });

      return sheets;
    }

    packAcrossSheets(rectangles, binWidth, binHeight) {
      if (!rectangles.length) return [];

      const uniform = this.tryUniformGridMultiSheet(rectangles, binWidth, binHeight);
      if (uniform) return uniform;

      const sorters = [
        (a, b) => (b.width * b.height) - (a.width * a.height),
        (a, b) => b.height - a.height || b.width - a.width,
        (a, b) => b.width - a.width || b.height - a.height,
        (a, b) => Math.max(b.width, b.height) - Math.max(a.width, a.height),
        (a, b) => (b.width + b.height) - (a.width + a.height),
      ];
      const heuristics = ['best-short-side', 'best-area', 'bottom-left', 'best-long-side'];
      let bestSolution = null;
      const seenOrders = new Set();

      for (const sorter of sorters) {
        const ordered = [...rectangles].sort(sorter);
        const signature = ordered.map((piece) => piece.id).join('|');
        if (seenOrders.has(signature)) continue;
        seenOrders.add(signature);

        for (const heuristic of heuristics) {
          const solution = this.packAcrossSheetsWithStrategy(ordered, binWidth, binHeight, heuristic);
          if (!solution) continue;

          if (!bestSolution || solution.length < bestSolution.length) {
            bestSolution = solution;
          } else if (bestSolution && solution.length === bestSolution.length) {
            const candidateLast = solution.at(-1)?.placements.length || 0;
            const bestLast = bestSolution.at(-1)?.placements.length || 0;
            if (candidateLast > bestLast) bestSolution = solution;
          }
        }
      }

      return bestSolution;
    }

    packAcrossSheetsWithStrategy(rectangles, binWidth, binHeight, heuristic) {
      const sheets = [];

      for (const rectangle of rectangles) {
        let best = null;

        for (let sheetIndex = 0; sheetIndex < sheets.length; sheetIndex += 1) {
          const sheet = sheets[sheetIndex];
          const placement = this.findBestFixedPlacement(rectangle, sheet.freeRectangles, heuristic);
          if (!placement) continue;

          const candidateScore = [...placement.score, sheetIndex];
          if (!best || this.compareScores(candidateScore, best.score) < 0) {
            best = { sheetIndex, placement, score: candidateScore };
          }
        }

        if (!best) {
          const freeRectangles = [{ x: 0, y: 0, width: binWidth, height: binHeight }];
          const placement = this.findBestFixedPlacement(rectangle, freeRectangles, heuristic);
          if (!placement) return null;

          sheets.push({ freeRectangles, placements: [] });
          best = { sheetIndex: sheets.length - 1, placement, score: placement.score };
        }

        const target = sheets[best.sheetIndex];
        const used = best.placement;
        target.placements.push({
          ...rectangle,
          x: used.x,
          y: used.y,
          rotated: false,
        });
        target.freeRectangles = this.splitFreeRectangles(target.freeRectangles, used);
      }

      return sheets.map((sheet) => ({ placements: sheet.placements }));
    }

    checkSheetFit(cuts, sheet) {
      const built = this.buildPackingPieces(cuts, sheet);
      if (built.error) {
        return { fits: false, reason: 'piece-limit', message: built.error };
      }

      const { pieces, totalPieces, kerf, sheetShort, sheetLong } = built;
      const binWidth = sheetShort + kerf;
      const binHeight = sheetLong + kerf;
      const sheets = this.packAcrossSheets(pieces, binWidth, binHeight);

      if (!sheets || !sheets.length) {
        return {
          fits: false,
          reason: 'packing',
          message: `The requested cuts could not be nested on the selected ${sheet.label} sheets. Please review the dimensions or call 303-781-1171 for assistance.`,
        };
      }

      return {
        fits: true,
        sheet,
        sheetShort,
        sheetLong,
        kerf,
        totalPieces,
        sheetCount: sheets.length,
        sheets,
        placements: sheets.flatMap((sheetLayout, sheetIndex) =>
          sheetLayout.placements.map((placement) => ({ ...placement, sheetIndex }))
        ),
      };
    }

    findBestFixedPlacement(rectangle, freeRectangles, heuristic) {
      let best = null;

      for (const free of freeRectangles) {
        if (rectangle.width > free.width + 1e-7 || rectangle.height > free.height + 1e-7) continue;

        const candidate = {
          x: free.x,
          y: free.y,
          width: rectangle.width,
          height: rectangle.height,
          rotated: false,
        };
        candidate.score = this.getPlacementScore(free, candidate, heuristic);

        if (!best || this.compareScores(candidate.score, best.score) < 0) best = candidate;
      }

      return best;
    }

    getPlacementScore(free, used, heuristic) {
      const leftoverWidth = Math.max(0, free.width - used.width);
      const leftoverHeight = Math.max(0, free.height - used.height);
      const shortSide = Math.min(leftoverWidth, leftoverHeight);
      const longSide = Math.max(leftoverWidth, leftoverHeight);
      const area = Math.max(0, (free.width * free.height) - (used.width * used.height));
      const bottom = used.y + used.height;

      if (heuristic === 'best-area') return [area, shortSide, longSide, used.y, used.x];
      if (heuristic === 'bottom-left') return [bottom, used.x, shortSide, area, longSide];
      if (heuristic === 'best-long-side') return [longSide, shortSide, area, used.y, used.x];
      return [shortSide, longSide, area, used.y, used.x];
    }

    compareScores(a, b) {
      for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
        const difference = (a[index] || 0) - (b[index] || 0);
        if (Math.abs(difference) > 1e-9) return difference;
      }
      return 0;
    }

    splitFreeRectangles(freeRectangles, used) {
      const next = [];
      const usedRight = used.x + used.width;
      const usedBottom = used.y + used.height;

      for (const free of freeRectangles) {
        const freeRight = free.x + free.width;
        const freeBottom = free.y + free.height;
        const intersects = !(
          used.x >= freeRight - 1e-9
          || usedRight <= free.x + 1e-9
          || used.y >= freeBottom - 1e-9
          || usedBottom <= free.y + 1e-9
        );

        if (!intersects) {
          next.push(free);
          continue;
        }

        if (used.x > free.x + 1e-9) {
          next.push({ x: free.x, y: free.y, width: used.x - free.x, height: free.height });
        }
        if (usedRight < freeRight - 1e-9) {
          next.push({ x: usedRight, y: free.y, width: freeRight - usedRight, height: free.height });
        }
        if (used.y > free.y + 1e-9) {
          next.push({ x: free.x, y: free.y, width: free.width, height: used.y - free.y });
        }
        if (usedBottom < freeBottom - 1e-9) {
          next.push({ x: free.x, y: usedBottom, width: free.width, height: freeBottom - usedBottom });
        }
      }

      return this.pruneFreeRectangles(next);
    }

    pruneFreeRectangles(rectangles) {
      const valid = rectangles.filter((rectangle) => rectangle.width > 1e-7 && rectangle.height > 1e-7);
      const keep = new Array(valid.length).fill(true);

      for (let i = 0; i < valid.length; i += 1) {
        if (!keep[i]) continue;
        for (let j = 0; j < valid.length; j += 1) {
          if (i === j || !keep[j]) continue;
          if (this.rectangleContains(valid[j], valid[i])) {
            keep[i] = false;
            break;
          }
        }
      }

      return valid.filter((_, index) => keep[index]);
    }

    rectangleContains(outer, inner) {
      return inner.x >= outer.x - 1e-9
        && inner.y >= outer.y - 1e-9
        && inner.x + inner.width <= outer.x + outer.width + 1e-9
        && inner.y + inner.height <= outer.y + outer.height + 1e-9;
    }

    readRows({ validate = false, sheet = this.getSheetSize() } = {}) {
      const rows = [...this.rowsElement.querySelectorAll('[data-cut-row-v2]')];
      let firstInvalid = null;
      const errors = [];

      const cuts = rows.map((row, index) => {
        const widthInput = row.querySelector('[data-cut-width-v2]');
        const lengthInput = row.querySelector('[data-cut-length-v2]');
        const piecesInput = row.querySelector('[data-cut-pieces-v2]');
        const toleranceInput = row.querySelector('[data-cut-tolerance-v2]');
        const width = Number.parseFloat(widthInput.value);
        const length = Number.parseFloat(lengthInput.value);
        const pieces = Number.parseInt(piecesInput.value, 10);
        const tolerance = toleranceInput.value.trim();
        const packing = (Number.isFinite(width) && Number.isFinite(length) && tolerance)
          ? this.getPackingDimensions(width, length, tolerance)
          : { width, length, adjustment: 0 };

        [widthInput, lengthInput, piecesInput, toleranceInput].forEach((input) => input.classList.remove('input-error'));

        const mark = (input, message) => {
          errors.push(`Cut ${index + 1}: ${message}`);
          firstInvalid ||= input;
          if (validate) input.classList.add('input-error');
        };

        if (!Number.isFinite(width) || width <= 0) mark(widthInput, 'enter a valid width.');
        if (!Number.isFinite(length) || length <= 0) mark(lengthInput, 'enter a valid length.');

        if (tolerance && Number.isFinite(packing.width) && Number.isFinite(packing.length)
          && (packing.width <= 0 || packing.length <= 0)) {
          mark(widthInput, 'the selected tolerance makes the finished cut dimension zero or less.');
          if (validate) lengthInput.classList.add('input-error');
        }

        if (tolerance && Number.isFinite(packing.width) && packing.width > 0
          && Number.isFinite(packing.length) && packing.length > 0
          && !this.pieceFitsSheet(packing.width, packing.length, sheet)) {
          mark(widthInput, this.getOversizeSupportMessage(sheet));
          widthInput.classList.add('input-error');
          lengthInput.classList.add('input-error');
        }

        if (!Number.isInteger(pieces) || pieces < 1) mark(piecesInput, 'pieces must be a whole number of 1 or more.');
        if (!tolerance) mark(toleranceInput, 'select a tolerance.');

        return {
          number: index + 1,
          width,
          length,
          pieces,
          tolerance,
          packingWidth: packing.width,
          packingLength: packing.length,
          toleranceAdjustment: packing.adjustment,
        };
      });

      return { cuts, errors, firstInvalid };
    }

    syncB2BDiscountTableForVariant() {
      const tableBody = this.productInfo?.querySelector('tbody[data-company_gid]');
      if (!tableBody) return;

      const companyId = tableBody.querySelector('.table-price-b2b')?.dataset.companyid || '';
      const variantId = String(this.productInfo?.querySelector('.product-variant-id')?.value || '');
      const entries = [...this.productInfo.querySelectorAll('.bulk_discount_entry')];
      const entry = entries.find((item) => item.dataset.company === companyId && item.dataset.variant === variantId);

      const defaults = {
        1: Number.parseFloat(tableBody.getAttribute('data-def-1-5')) || 0,
        2: Number.parseFloat(tableBody.getAttribute('data-def-6-10')) || 0,
        3: Number.parseFloat(tableBody.getAttribute('data-def-11-20')) || 0,
        4: Number.parseFloat(tableBody.getAttribute('data-def-20-plus')) || 0,
      };

      const discounts = entry ? {
        1: Number.parseFloat(entry.dataset.discount_for_1_to_5) || 0,
        2: Number.parseFloat(entry.dataset.discount_for_6_to_10) || 0,
        3: Number.parseFloat(entry.dataset.discount_for_11_to_20) || 0,
        4: Number.parseFloat(entry.dataset.discount_for_20) || 0,
      } : defaults;

      Object.entries(discounts).forEach(([position, percent]) => {
        const row = tableBody.querySelector(`.discount-table-row[data-pos="${position}"]`);
        const priceCell = row?.querySelector('.table-price-b2b');
        const savingsCell = row?.querySelector('.savings');
        if (priceCell) priceCell.dataset.off = String(percent);
        if (savingsCell) savingsCell.textContent = `(${percent}%)`;
      });
    }

    getDiscount(total) {
      this.syncB2BDiscountTableForVariant();
      const tableBody = this.productInfo?.querySelector('tbody[data-company_gid]');
      if (!tableBody) return { amount: total, percent: 0 };

      const quantity = Number.parseInt(this.productInfo.querySelector('.qty__input')?.value, 10) || 1;
      let position = 1;
      if (quantity > 5 && quantity <= 10) position = 2;
      else if (quantity > 10 && quantity < 20) position = 3;
      else if (quantity >= 20) position = 4;

      tableBody.querySelectorAll('.discount-table-row').forEach((row) => row.classList.remove('selected'));
      tableBody.querySelector(`.discount-table-row[data-pos="${position}"]`)?.classList.add('selected');

      tableBody.querySelectorAll('.discount-table-row').forEach((row) => {
        const percent = Number.parseFloat(row.querySelector('.table-price-b2b')?.dataset.off) || 0;
        const price = total - (total * percent / 100);
        const priceCell = row.querySelector('.table-price-b2b');
        if (priceCell) priceCell.textContent = this.formatMoney(price);
      });

      const selectedCell = tableBody.querySelector(`.discount-table-row[data-pos="${position}"] .table-price-b2b`);
      const percent = Number.parseFloat(selectedCell?.dataset.off) || 0;
      return { amount: total - (total * percent / 100), percent };
    }

    calculate({ validate = false, focusError = false } = {}) {
      const sheet = this.getSheetSize();

      // No cut dimensions entered = customer is buying one complete selected sheet.
      // Show the full-sheet material price immediately and do not require cut fields.
      if (!this.hasCutIntent()) {
        this.clearCutInputErrors();
        this.hideMessage();
        this.setAddToCartEnabled(true);

        const unitPrice = this.getUnitPrice();
        const materialTotal = this.getFullSheetMaterialPrice(sheet);
        const cutCharge = 0;
        const subtotal = materialTotal;
        const discounted = this.getDiscount(subtotal);
        const finalTotal = discounted.amount;
        const packing = {
          fits: true,
          placements: [],
          sheets: [],
          sheetCount: 1,
          sheet,
          sheetShort: Math.min(sheet.width, sheet.length),
          sheetLong: Math.max(sheet.width, sheet.length),
          kerf: this.config.kerf,
          totalPieces: 0,
          fullSheet: true,
        };

        const result = {
          unitPrice,
          cuts: [],
          materialTotal,
          cutCharge,
          subtotal,
          discountPercent: discounted.percent,
          finalTotal,
          totalPieces: 0,
          sheet,
          packing,
          fullSheet: true,
        };

        this.lastResult = result;
        this.updateDisplay(result);
        this.updateProperties(result);
        return result;
      }

      const { cuts, errors, firstInvalid } = this.readRows({ validate, sheet });

      if (errors.length) {
        this.setAddToCartEnabled(false);
        this.resetCalculatedState();

        const persistentOversizeError = errors.find((error) =>
          error.includes('For sizes larger than 48" x 96"')
        );

        if (validate || persistentOversizeError) {
          this.showMessage(persistentOversizeError || errors[0]);
          if (validate && focusError && firstInvalid) {
            firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
            firstInvalid.focus({ preventScroll: true });
          }
        } else {
          this.hideMessage();
        }
        return null;
      }

      const packing = this.checkSheetFit(cuts, sheet);
      if (!packing.fits) {
        this.setAddToCartEnabled(false);
        this.resetCalculatedState();
        this.showMessage(packing.message);

        if (focusError) {
          this.builder.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return null;
      }

      this.hideMessage();
      this.setAddToCartEnabled(true);

      // Charge the number of complete selected sheets required by the nesting layout.
      const unitPrice = this.getUnitPrice();
      const materialPricePerSheet = this.getFullSheetMaterialPrice(sheet);
      const sheetCount = Math.max(1, packing.sheetCount || 1);
      const materialTotal = materialPricePerSheet * sheetCount;
      let cutCharge = 0;
      let totalPieces = 0;

      const calculatedCuts = cuts.map((cut) => {
        const billableWidth = cut.width;
        const billableLength = cut.length;
        const squareFeetPerPiece = (cut.width * cut.length) / 144;
        const materialPrice = 0;
        const cutRate = this.getCutRate(cut.pieces);
        const rowCutCharge = cutRate * cut.pieces;
        const rowTotal = rowCutCharge;

        cutCharge += rowCutCharge;
        totalPieces += cut.pieces;

        return { ...cut, billableWidth, billableLength, squareFeetPerPiece, materialPrice, cutRate, cutCharge: rowCutCharge, rowTotal };
      });

      const subtotal = materialTotal + cutCharge;
      const discounted = this.getDiscount(subtotal);
      const finalTotal = discounted.amount;

      const result = {
        unitPrice,
        cuts: calculatedCuts,
        materialTotal,
        cutCharge,
        subtotal,
        discountPercent: discounted.percent,
        finalTotal,
        totalPieces,
        sheet,
        sheetCount,
        materialPricePerSheet,
        packing,
      };
      this.lastResult = result;
      this.updateDisplay(result);
      this.updateProperties(result);
      return result;
    }

    resetCalculatedState() {
      this.lastResult = null;
      this.updateDisplay({ materialTotal: 0, cutCharge: 0, finalTotal: 0, sheetCount: 0, cuts: [], packing: null });

      [
        '[data-v2-prop-total]',
        '[data-v2-prop-cut-charge]',
        '[data-v2-prop-cut-data]',
        '[data-v2-prop-cut-details]',
        '[data-v2-prop-cut-count]',
        '[data-v2-prop-total-pieces]',
        '[data-v2-prop-material-price]',
      ].forEach((selector) => {
        const input = this.form.querySelector(selector);
        if (input) input.value = '';
      });

      if (this.propertiesContainer) this.propertiesContainer.innerHTML = '';
    }

    setAddToCartEnabled(enabled) {
      const button = this.form.querySelector('button[type="submit"][name="add"]');
      if (!button) return;

      if (!enabled) {
        button.dataset.cutValidationBlocked = 'true';
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        return;
      }

      if (button.dataset.cutValidationBlocked !== 'true') return;
      delete button.dataset.cutValidationBlocked;

      const variantInput = this.form.querySelector('.product-variant-id');
      const canPurchase = this.currentVariantAvailable !== false
        && Boolean(variantInput?.value && !variantInput.disabled);

      if (canPurchase) {
        button.disabled = false;
        button.removeAttribute('aria-disabled');
      }
    }

    updateDisplay(result) {
      if (this.materialElement) this.materialElement.textContent = this.formatMoney(result.materialTotal);
      if (this.cutChargeElement) this.cutChargeElement.textContent = this.formatMoney(result.cutCharge);
      if (this.totalElement) this.totalElement.textContent = this.formatMoney(result.finalTotal);
      if (this.sheetCountElement) {
        const count = Number(result.sheetCount || result.packing?.sheetCount || 0);
        this.sheetCountElement.textContent = count ? `${count} full sheet${count === 1 ? '' : 's'}` : '—';
      }
      if (this.mainTotalElement) {
        this.mainTotalElement.textContent = this.formatMoney(result.finalTotal);
        this.mainTotalElement.dataset.total = Number(result.finalTotal || 0).toFixed(2);
      }

      const breakdown = this.builder.querySelector('[data-cut-breakdown-v2]');
      if (breakdown) breakdown.hidden = this.builder.dataset.showBreakdown === 'false';

      this.renderSheetLayouts(result);
    }

    updateProperties(result) {
      const set = (selector, value) => {
        const input = this.form.querySelector(selector);
        if (input) input.value = value;
      };

      const publicDetails = result.cuts.map((cut) =>
        `Cut ${cut.number}: ${this.cleanNumber(cut.width)}" W x ${this.cleanNumber(cut.length)}" L, ${cut.pieces} piece${cut.pieces === 1 ? '' : 's'}, ${cut.tolerance}`
      ).join(' | ');

      const data = result.cuts.map((cut) => ({
        number: cut.number,
        width: cut.width,
        length: cut.length,
        pieces: cut.pieces,
        tolerance: cut.tolerance,
        billableWidth: cut.billableWidth,
        billableLength: cut.billableLength,
        packingWidth: Number(cut.packingWidth.toFixed(3)),
        packingLength: Number(cut.packingLength.toFixed(3)),
        toleranceAdjustment: Number(cut.toleranceAdjustment.toFixed(3)),
        cutRate: Number(cut.cutRate.toFixed(2)),
      }));

      set('[data-v2-prop-total]', Number(result.finalTotal || 0).toFixed(2));
      set('[data-v2-prop-cut-charge]', Number(result.cutCharge || 0).toFixed(2));
      set('[data-v2-prop-cut-data]', JSON.stringify(data));
      set('[data-v2-prop-cut-details]', publicDetails);
      set('[data-v2-prop-cut-count]', result.cuts.length ? String(result.cuts.length) : '');
      set('[data-v2-prop-total-pieces]', result.cuts.length ? String(result.totalPieces) : '');
      set('[data-v2-prop-material-price]', Number(result.materialTotal || 0).toFixed(2));

      if (this.propertiesContainer) {
        this.propertiesContainer.innerHTML = '';
        const addProperty = (name, value) => {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = `properties[${name}]`;
          input.value = value;
          this.propertiesContainer.appendChild(input);
          return input;
        };

        result.cuts.forEach((cut) => {
          addProperty(`Cut ${cut.number} Details`, `${this.cleanNumber(cut.width)}" W x ${this.cleanNumber(cut.length)}" L`);
          addProperty(`Cut ${cut.number} Pieces`, String(cut.pieces));
          addProperty(`Cut ${cut.number} Tolerance`, cut.tolerance);
        });

        if (result.cuts.length && result.packing?.sheets?.length) {
          const sheetCount = result.packing.sheetCount || result.packing.sheets.length;
          addProperty('Sheets Required', String(sheetCount));
          addProperty('Sheet Size', result.sheet?.label || '48" x 96"');
          addProperty('Material Price Per Sheet', Number(result.materialPricePerSheet || 0).toFixed(2));

          result.packing.sheets.forEach((sheetLayout, index) => {
            addProperty(`Sheet ${index + 1} Allocation`, this.getSheetAllocationText(sheetLayout));
          });

          addProperty('_sheetCount', String(sheetCount));
          addProperty('_sheetLayoutData', JSON.stringify(this.getCompactSheetLayoutData(result)));

        }
      }
    }

    getSheetAllocationText(sheetLayout) {
      const counts = new Map();
      (sheetLayout?.placements || []).forEach((placement) => {
        counts.set(placement.cutNumber, (counts.get(placement.cutNumber) || 0) + 1);
      });
      return [...counts.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([cutNumber, count]) => `Cut ${cutNumber} x ${count}`)
        .join(', ');
    }

    getCompactSheetLayoutData(result) {
      return (result.packing?.sheets || []).map((sheetLayout, sheetIndex) => ({
        sheet: sheetIndex + 1,
        allocations: this.getSheetAllocationText(sheetLayout),
        placements: (sheetLayout.placements || []).map((placement) => ({
          c: placement.cutNumber,
          x: Number(placement.x.toFixed(3)),
          y: Number(placement.y.toFixed(3)),
          w: Number(placement.packingShort.toFixed(3)),
          h: Number(placement.packingLong.toFixed(3)),
        })),
      }));
    }

    renderSheetLayouts(result) {
      if (!this.layoutContainer) return;

      if (this.layoutPdfUrl) {
        URL.revokeObjectURL(this.layoutPdfUrl);
        this.layoutPdfUrl = null;
      }
      this.currentLayoutPdfBlob = null;
      this.layoutContainer.innerHTML = '';

      const sheets = result?.packing?.sheets;
      if (!Array.isArray(sheets) || !sheets.length || !result?.cuts?.length) {
        this.layoutContainer.hidden = true;
        return;
      }

      const sheetCount = result.packing.sheetCount || sheets.length;
      const heading = document.createElement('div');
      heading.className = 'custom-cut-layout-v2__heading';

      const headingCopy = document.createElement('div');
      const title = document.createElement('strong');
      title.textContent = `${sheetCount} full sheet${sheetCount === 1 ? '' : 's'} required`;
      const note = document.createElement('span');
      note.textContent = `Cut layout preview • ${this.cleanNumber(result.packing.kerf)}" blade kerf included`;
      headingCopy.append(title, note);

      const download = document.createElement('a');
      download.className = 'custom-cut-layout-v2__download';
      download.textContent = 'Download layout PDF';
      download.setAttribute('download', `cut-layout-${sheetCount}-sheet${sheetCount === 1 ? '' : 's'}.pdf`);

      heading.append(headingCopy, download);
      this.layoutContainer.appendChild(heading);

      const grid = document.createElement('div');
      grid.className = 'custom-cut-layout-v2__grid';
      sheets.forEach((sheetLayout, sheetIndex) => {
        grid.appendChild(this.createSheetLayoutCard(result, sheetLayout, sheetIndex));
      });
      this.layoutContainer.appendChild(grid);

      const footer = document.createElement('p');
      footer.className = 'custom-cut-layout-v2__note';
      footer.textContent = 'Layout is a nesting plan for material usage; shop cut sequence may be adjusted during production.';
      this.layoutContainer.appendChild(footer);

      this.currentLayoutPdfBlob = this.buildLayoutPdfBlob(result);
      if (this.currentLayoutPdfBlob) {
        this.layoutPdfUrl = URL.createObjectURL(this.currentLayoutPdfBlob);
        download.href = this.layoutPdfUrl;
      } else {
        download.hidden = true;
      }

      this.layoutContainer.hidden = false;
    }

    createSheetLayoutCard(result, sheetLayout, sheetIndex) {
      const card = document.createElement('section');
      card.className = 'custom-cut-layout-v2__card';

      const header = document.createElement('div');
      header.className = 'custom-cut-layout-v2__card-header';
      const title = document.createElement('strong');
      title.textContent = `Sheet ${sheetIndex + 1}`;
      const meta = document.createElement('span');
      const pieceCount = sheetLayout.placements?.length || 0;
      meta.textContent = `${pieceCount} piece${pieceCount === 1 ? '' : 's'} • ${result.sheet.label}`;
      header.append(title, meta);
      card.appendChild(header);

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      const sheetLong = result.packing.sheetLong || Math.max(result.sheet.width, result.sheet.length);
      const sheetShort = result.packing.sheetShort || Math.min(result.sheet.width, result.sheet.length);
      svg.setAttribute('viewBox', `0 0 ${sheetLong} ${sheetShort}`);
      svg.setAttribute('role', 'img');
      svg.setAttribute('aria-label', `Cut layout for sheet ${sheetIndex + 1}`);
      svg.classList.add('custom-cut-layout-v2__svg');

      const border = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      border.setAttribute('x', '0');
      border.setAttribute('y', '0');
      border.setAttribute('width', String(sheetLong));
      border.setAttribute('height', String(sheetShort));
      border.setAttribute('class', 'custom-cut-layout-v2__sheet-outline');
      svg.appendChild(border);

      (sheetLayout.placements || []).forEach((placement) => {
        // Packing engine stores the short sheet axis as x and long axis as y.
        // The preview rotates the sheet landscape for readability.
        const x = placement.y;
        const y = placement.x;
        const width = placement.packingLong;
        const height = placement.packingShort;
        const group = document.createElementNS('http://www.w3.org/2000/svg', 'g');

        const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
        rect.setAttribute('x', String(x));
        rect.setAttribute('y', String(y));
        rect.setAttribute('width', String(Math.max(0.001, width)));
        rect.setAttribute('height', String(Math.max(0.001, height)));
        rect.setAttribute('class', 'custom-cut-layout-v2__piece');
        rect.style.setProperty('--cut-index', String((placement.cutNumber - 1) % 6));

        const tooltip = document.createElementNS('http://www.w3.org/2000/svg', 'title');
        tooltip.textContent = `Cut ${placement.cutNumber}, piece ${placement.pieceNumber}: ${this.cleanNumber(placement.nominalWidth)}" x ${this.cleanNumber(placement.nominalHeight)}"`;
        rect.appendChild(tooltip);
        group.appendChild(rect);

        if (width >= 8 && height >= 4) {
          const label = document.createElementNS('http://www.w3.org/2000/svg', 'text');
          label.setAttribute('x', String(x + width / 2));
          label.setAttribute('y', String(y + height / 2));
          label.setAttribute('class', 'custom-cut-layout-v2__piece-label');
          label.textContent = `C${placement.cutNumber}`;
          group.appendChild(label);
        }

        svg.appendChild(group);
      });

      card.appendChild(svg);
      return card;
    }

    buildLayoutPdfBlob(result) {
      const sheets = result?.packing?.sheets;
      if (!Array.isArray(sheets) || !sheets.length) return null;

      const ascii = (value) => String(value ?? '').replace(/[^\x20-\x7E]/g, ' ');
      const pdfEscape = (value) => ascii(value).replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
      const fmt = (value) => Number(value || 0).toFixed(2).replace(/\.00$/, '');
      const objects = {};
      const pageIds = [];
      const fontId = 3;
      const pageWidth = 792;
      const pageHeight = 612;
      const margin = 36;
      const sheetLong = result.packing.sheetLong || Math.max(result.sheet.width, result.sheet.length);
      const sheetShort = result.packing.sheetShort || Math.min(result.sheet.width, result.sheet.length);
      const drawingWidth = pageWidth - margin * 2;
      const drawingHeight = 360;
      const scale = Math.min(drawingWidth / sheetLong, drawingHeight / sheetShort);
      const drawW = sheetLong * scale;
      const drawH = sheetShort * scale;
      const baseX = margin + (drawingWidth - drawW) / 2;
      const baseY = 120;

      sheets.forEach((sheetLayout, index) => {
        const pageId = 4 + index * 2;
        const contentId = pageId + 1;
        pageIds.push(pageId);

        const commands = [];
        commands.push('0.15 0.15 0.15 rg');
        commands.push(`BT /F1 18 Tf ${margin} ${pageHeight - 48} Td (Cut Layout - Sheet ${index + 1} of ${sheets.length}) Tj ET`);
        commands.push(`BT /F1 10 Tf ${margin} ${pageHeight - 68} Td (${pdfEscape(result.sheet.label)} | Kerf ${pdfEscape(this.cleanNumber(result.packing.kerf))} in | ${sheetLayout.placements.length} pieces) Tj ET`);
        commands.push('0.85 0.85 0.85 rg 0.55 0.55 0.55 RG 1 w');
        commands.push(`${fmt(baseX)} ${fmt(baseY)} ${fmt(drawW)} ${fmt(drawH)} re S`);

        (sheetLayout.placements || []).forEach((placement) => {
          const visualX = placement.y;
          const visualY = placement.x;
          const visualW = placement.packingLong;
          const visualH = placement.packingShort;
          const x = baseX + visualX * scale;
          const y = baseY + (sheetShort - visualY - visualH) * scale;
          const w = visualW * scale;
          const h = visualH * scale;
          const shade = 0.92 - ((placement.cutNumber - 1) % 5) * 0.035;
          commands.push(`${shade.toFixed(3)} 0.86 ${shade.toFixed(3)} rg 0.57 0.15 0.56 RG 0.5 w`);
          commands.push(`${fmt(x)} ${fmt(y)} ${fmt(w)} ${fmt(h)} re B`);
          if (w >= 28 && h >= 14) {
            commands.push('0.25 0.12 0.25 rg');
            commands.push(`BT /F1 7 Tf ${fmt(x + 3)} ${fmt(y + Math.max(4, h / 2 - 2))} Td (C${placement.cutNumber}) Tj ET`);
          }
        });

        commands.push('0.2 0.2 0.2 rg');
        commands.push(`BT /F1 9 Tf ${margin} 80 Td (${pdfEscape(this.getSheetAllocationText(sheetLayout))}) Tj ET`);
        commands.push(`BT /F1 8 Tf ${margin} 62 Td (Generated from the customer cut calculator. Shop cut sequence may be adjusted during production.) Tj ET`);

        const stream = commands.join('\n') + '\n';
        objects[contentId] = `<< /Length ${stream.length} >>\nstream\n${stream}endstream`;
        objects[pageId] = `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /Font << /F1 ${fontId} 0 R >> >> /Contents ${contentId} 0 R >>`;
      });

      objects[1] = '<< /Type /Catalog /Pages 2 0 R >>';
      objects[2] = `<< /Type /Pages /Kids [${pageIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageIds.length} >>`;
      objects[fontId] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>';

      const maxId = Math.max(...Object.keys(objects).map(Number));
      let pdf = '%PDF-1.4\n%Plasticare Cut Layout\n';
      const offsets = new Array(maxId + 1).fill(0);

      for (let id = 1; id <= maxId; id += 1) {
        if (!objects[id]) continue;
        offsets[id] = pdf.length;
        pdf += `${id} 0 obj\n${objects[id]}\nendobj\n`;
      }

      const xrefOffset = pdf.length;
      pdf += `xref\n0 ${maxId + 1}\n`;
      pdf += '0000000000 65535 f \n';
      for (let id = 1; id <= maxId; id += 1) {
        pdf += `${String(offsets[id]).padStart(10, '0')} 00000 n \n`;
      }
      pdf += `trailer\n<< /Size ${maxId + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;

      return new Blob([pdf], { type: 'application/pdf' });
    }

    cleanNumber(value) {
      return Number.isFinite(value) ? String(Number(value.toFixed(3))) : '';
    }

    showMessage(message) {
      if (!this.messageElement) return;
      this.messageElement.textContent = message;
      this.messageElement.hidden = false;
    }

    hideMessage() {
      if (!this.messageElement) return;
      this.messageElement.textContent = '';
      this.messageElement.hidden = true;
    }

    formatMoney(amount) {
      try {
        if (window.Shopify && typeof Shopify.formatMoney === 'function') {
          const format = window.theme?.moneyFormat || '${{amount}}';
          return Shopify.formatMoney(Math.round((amount || 0) * 100), format);
        }
      } catch (_) {}

      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(amount || 0);
    }

    destroy() {
      this.unsubscribeVariant?.();
      this.priceObserver?.disconnect();
      if (this.layoutPdfUrl) URL.revokeObjectURL(this.layoutPdfUrl);
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((builder) => {
      if (builder.dataset.initialized === 'true') return;
      builder.customCutCalculatorV2 = new CustomCutCalculatorV2(builder);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll(SELECTOR).forEach((builder) => builder.customCutCalculatorV2?.destroy());
  });
})();
